import { parseJson } from '@gnsx/genesys.js';
import { isBrowser } from 'es-toolkit/predicate';

/** SSR-safe localStorage helper with type-safe methods. */
export const localStorageHelper = {
  isAvailable(): boolean {
    return isBrowser();
  },

  get(key: string): string | null {
    if (!this.isAvailable()) return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },

  set(key: string, value: string): boolean {
    if (!this.isAvailable()) return false;
    try {
      localStorage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  },

  remove(key: string): void {
    if (!this.isAvailable()) return;
    try {
      localStorage.removeItem(key);
    } catch {
      // Silently fail
    }
  },

  clear(): void {
    if (!this.isAvailable()) return;
    try {
      localStorage.clear();
    } catch {
      // Silently fail
    }
  },

  getBoolean(key: string, defaultValue = false): boolean {
    const value = this.get(key);
    if (value === null) return defaultValue;
    const lower = value.toLowerCase();
    if (lower === 'true') return true;
    if (lower === 'false') return false;
    return defaultValue;
  },

  setBoolean(key: string, value: boolean): boolean {
    return this.set(key, String(value));
  },

  getNumber(key: string, defaultValue?: number): number | null {
    const value = this.get(key);
    if (value === null) return defaultValue ?? null;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? (defaultValue ?? null) : parsed;
  },

  setNumber(key: string, value: number): boolean {
    return this.set(key, String(value));
  },

  getJson<T = unknown>(key: string): T | null {
    const value = this.get(key);
    if (value === null) return null;
    try {
      return parseJson(value) as T;
    } catch {
      return null;
    }
  },

  setJson(key: string, value: unknown): boolean {
    try {
      const serialized = JSON.stringify(value);
      return this.set(key, serialized);
    } catch {
      return false;
    }
  },

  removeIf(predicate: (key: string) => boolean): void {
    if (!this.isAvailable()) return;
    try {
      for (const key of Object.keys(localStorage)) {
        if (predicate(key)) {
          localStorage.removeItem(key);
        }
      }
    } catch {
      // Silently fail
    }
  },
};

/** Usage examples:
 * localStorageHelper.getBoolean('genesys-showEngineAssets', true);
 * localStorageHelper.setJson('genesys-viewportSettings', { showGrid: true, fov: 75 });
 * localStorageHelper.getJson<{ showGrid: boolean; fov: number }>('genesys-viewportSettings');
 * localStorageHelper.getNumber('genesys-thumbnailStep', 2);
 * localStorageHelper.setBoolean('genesys-sidebarCollapsed', true);
 * localStorageHelper.removeIf(key => key.startsWith('react-resizable-panels:genesys-editor-layout'));
 */
